import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, X, ArrowLeft, Filter, Download } from 'lucide-react';
import { Input } from '../../Components/UI/input';
import { cn } from '../../Lib/Cn';
import useIsMobile from '../../Hooks/useIsMobile';
import Threads from '../Threads/Threads';
import ThreadsReport from '../Threads/ThreadsReport';
import Connections from '../Connections/Connections';
import { updateHeaderConfig, getAgencyDetails, userIsAdmin } from '../../Store/Reducers';
import { checkUserRights, formatAmount, safeParseJsonArray } from '../../Utils/Utilities';
import { getAuthData } from '../../Utils/AuthUtils';
import { useCurrency } from '../../Hooks/useCurrency';
import { getThreadsAPI } from '../Threads/ThreadsAPI';
import { checkAPIResponse } from '../../Utils/APIResponseUtils';
import { triggerHaptic } from '../../Hooks/useHaptics';
import { canShowImportInquiries } from '../../Utils/FeatureFlags';

const Growth = () => {
    const isMobile = useIsMobile();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const searchParams = new URLSearchParams(location.search);
    const initialTab = searchParams.get('tab') || 'inquiries';

    const [activeTab, setActiveTab] = useState(initialTab); // 'inquiries' | 'sales' | 'contacts'
    const [showSearch, setShowSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tabFilterActive, setTabFilterActive] = useState({
        inquiries: false,
        sales: false,
        contacts: false,
    });
    const [inquiriesFilterCount, setInquiriesFilterCount] = useState(0);
    const [inquiriesFilterAmount, setInquiriesFilterAmount] = useState(null);
    const [salesFilterCount, setSalesFilterCount] = useState(0);
    const [salesFilterAmount, setSalesFilterAmount] = useState(null);
    const [contactsFilterCount, setContactsFilterCount] = useState(0);
    const [contactsFilterAmount, setContactsFilterAmount] = useState(null);

    const agencyData = useSelector(getAgencyDetails);
    const isAdmin = useSelector(userIsAdmin);
    const authData = useMemo(() => getAuthData(), []);
    const currentUserID = Number(authData?.UserID || 0);

    const currencySymbol = useCurrency();
    const activeData = useSelector((state) => state.activeData);
    const threadData = useSelector((state) => state.threadData?.data || []);
    const connectionData = useSelector((state) => state.connectionData?.data || []);
    const userRights = checkUserRights();

    const handleFilterStateChange = (tab, isActive) => {
        setTabFilterActive(prev => {
            if (prev[tab] === isActive) return prev;
            return { ...prev, [tab]: isActive };
        });
    };

    const monthlyThreadsList = useMemo(() => {
        let raw = activeData?.data?.MonthlyThreads || activeData?.MonthlyThreads;
        if (!raw) {
            try {
                const dCookie = localStorage.getItem('D');
                if (dCookie) {
                    const parsedD = JSON.parse(dCookie);
                    const agencyData = Array.isArray(parsedD) ? parsedD[0] : parsedD;
                    raw = agencyData?.MonthlyThreads;
                }
            } catch (e) { }
        }
        if (!raw) return [];
        if (typeof raw === 'string') {
            try { return JSON.parse(raw); } catch (e) { return []; }
        }
        if (Array.isArray(raw)) return raw;
        return [];
    }, [activeData]);

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    // Live counts
    const getThreadType = (t) => Number(t.typeID ?? t.TypeID ?? t.tID ?? t.TID ?? t.type ?? 0);

    const getMonthlyStatsForType = (typeID) => {
        const matchingItems = monthlyThreadsList.filter(
            m => Number(m.TID) === typeID && Number(m.MM) === currentMonth && Number(m.YY) === currentYear
        );
        if (matchingItems.length === 0) {
            return { count: null, amount: null };
        }

        if (isAdmin) {
            const count = matchingItems.reduce((sum, item) => sum + Number(item.Rcvd || 0), 0);
            const amount = matchingItems.reduce((sum, item) => sum + Number(item.Amt || item.amt || 0), 0);
            return { count, amount };
        } else {
            let count = 0;
            let amount = 0;
            matchingItems.forEach(item => {
                const users = safeParseJsonArray(item.Usrs);
                const u = users.find(user => Number(user.UID) === currentUserID);
                if (u) {
                    count += Number(u.RC || 0);
                    amount += Number(u.RA || 0);
                }
            });
            return { count, amount };
        }
    };

    const inquiriesCount = useMemo(() => {
        if (tabFilterActive.inquiries || (inquiriesFilterCount > 0)) {
            return inquiriesFilterCount;
        }
        const threadsCount = (threadData || []).filter(
            t => (getThreadType(t) === 1) && !t.IsRemove && !t.IsDeleted && !t.isRemove && !t.isDeleted
        ).length;

        const monthlyStats = getMonthlyStatsForType(1);
        if (monthlyStats.count !== null && monthlyStats.count === threadsCount) {
            return monthlyStats.count;
        }
        return threadsCount;
    }, [tabFilterActive.inquiries, inquiriesFilterCount, monthlyThreadsList, currentMonth, currentYear, threadData, isAdmin, currentUserID]);

    const inquiriesAmount = useMemo(() => {
        if (inquiriesFilterAmount !== null) {
            return inquiriesFilterAmount;
        }
        const threadsAmount = (threadData || [])
            .filter(t => (getThreadType(t) === 1) && !t.IsRemove && !t.IsDeleted && !t.isRemove && !t.isDeleted)
            .reduce((sum, t) => sum + (Number(t.bizProspect || t.BizProspect || t.amount || t.Amt || 0) || 0), 0);

        const monthlyStats = getMonthlyStatsForType(1);
        if (monthlyStats.amount !== null && monthlyStats.amount === threadsAmount) {
            return monthlyStats.amount;
        }
        return threadsAmount;
    }, [inquiriesFilterAmount, monthlyThreadsList, currentMonth, currentYear, threadData, isAdmin, currentUserID]);

    const salesCount = useMemo(() => {
        if (tabFilterActive.sales || salesFilterCount > 0) {
            return salesFilterCount;
        }
        const threadsCount = (threadData || []).filter(
            t => (getThreadType(t) === 2) && !t.IsRemove && !t.IsDeleted && !t.isRemove && !t.isDeleted
        ).length;

        const monthlyStats = getMonthlyStatsForType(2);
        if (monthlyStats.count !== null && monthlyStats.count === threadsCount) {
            return monthlyStats.count;
        }
        return threadsCount;
    }, [tabFilterActive.sales, salesFilterCount, monthlyThreadsList, currentMonth, currentYear, threadData, isAdmin, currentUserID]);

    const salesAmount = useMemo(() => {
        if (salesFilterAmount !== null) {
            return salesFilterAmount;
        }
        const threadsAmount = (threadData || [])
            .filter(t => (getThreadType(t) === 2) && !t.IsRemove && !t.IsDeleted && !t.isRemove && !t.isDeleted)
            .reduce((sum, t) => sum + (Number(t.bizProspect || t.BizProspect || t.amount || t.Amt || 0) || 0), 0);

        const monthlyStats = getMonthlyStatsForType(2);
        if (monthlyStats.amount !== null && monthlyStats.amount === threadsAmount) {
            return monthlyStats.amount;
        }
        return threadsAmount;
    }, [salesFilterAmount, monthlyThreadsList, currentMonth, currentYear, threadData, isAdmin, currentUserID]);

    const contactsCount = useMemo(() => {
        if (tabFilterActive.contacts || contactsFilterCount > 0) {
            return contactsFilterCount;
        }
        return (connectionData || []).filter(c => !c.IsRemove && !c.IsDeleted && !c.isRemove && !c.isDeleted).length;
    }, [tabFilterActive.contacts, contactsFilterCount, connectionData]);

    const contactsAmount = useMemo(() => {
        if (contactsFilterAmount !== null) {
            return contactsFilterAmount;
        }
        return (connectionData || [])
            .filter(c => !c.IsRemove && !c.IsDeleted && !c.isRemove && !c.isDeleted)
            .reduce((sum, c) => sum + (Number(c.potential || c.Potential || 0) || 0), 0);
    }, [contactsFilterAmount, connectionData]);

    // Handle tab change and sync with searchParams (clears old filters when switching tabs)
    const handleTabChange = (tabId) => {
        if (activeTab !== tabId) {
            triggerHaptic("selection");
        }
        setActiveTab(tabId);
        navigate(`${location.pathname}?tab=${tabId}`, { replace: true });
    };

    // Sync activeTab with URL search params
    useEffect(() => {
        const currentTab = new URLSearchParams(location.search).get('tab');
        if (currentTab && currentTab !== activeTab) {
            setActiveTab(currentTab);
        }
    }, [location.search]);

    // If user lands on inquiries and inquiries count is 0, auto switch to Sales or Contacts
    useEffect(() => {
        const hasExplicitTabParam = Boolean(new URLSearchParams(location.search).get('tab'));
        if (!hasExplicitTabParam && activeTab === 'inquiries' && inquiriesCount === 0) {
            if (salesCount > 0) {
                handleTabChange('sales');
            } else if (contactsCount > 0) {
                handleTabChange('contacts');
            }
        }
    }, [inquiriesCount, salesCount, contactsCount, activeTab, location.search]);

    // Scroll to top whenever activeTab changes
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, [activeTab]);

    // Topbar header config for mobile
    useEffect(() => {
        if (!isMobile) return;
        const isCurrentTabActive = Boolean(tabFilterActive[activeTab]);
        const actionsRight = [
            {
                icon: Search,
                onClick: () => {
                    window.dispatchEvent(new CustomEvent('growth-toggle-search'));
                },
            },
            {
                icon: Filter,
                onClick: () => {
                    window.dispatchEvent(new CustomEvent('growth-toggle-filter'));
                },
                showBadge: isCurrentTabActive,
            },
            ...(activeTab === 'inquiries' && userRights?.addThr && canShowImportInquiries() ? [{
                icon: Download,
                onClick: () => {
                    window.dispatchEvent(new CustomEvent('inquiries-open-import'));
                },
            }] : [])
        ];

        dispatch(updateHeaderConfig({
            title: "Growth",
            iconLeft: {
                icon: ArrowLeft,
                onClick: () => navigate("/", { replace: true }),
            },
            actionsRight: actionsRight,
        }));
    }, [isMobile, activeTab, tabFilterActive, dispatch, navigate, userRights?.addThr]);

    // Listen to header events from Main.js
    useEffect(() => {
        const handleToggleSearch = () => {
            setShowSearch(prev => {
                if (prev) setSearchTerm('');
                return !prev;
            });
        };

        const handleToggleFilter = () => {
            // Forward filter toggle to the active child view
            window.dispatchEvent(new CustomEvent(`${activeTab}-toggle-filter`));
        };

        window.addEventListener('growth-toggle-search', handleToggleSearch);
        window.addEventListener('growth-toggle-filter', handleToggleFilter);

        return () => {
            window.removeEventListener('growth-toggle-search', handleToggleSearch);
            window.removeEventListener('growth-toggle-filter', handleToggleFilter);
        };
    }, [activeTab]);

    // Fetch initial Sales/Orders count/data on page mount so count updates immediately
    useEffect(() => {
        const fetchInitialSalesData = async () => {
            try {
                const now = new Date();
                const currentMonth = String(now.getMonth() + 1);
                const currentYear = String(now.getFullYear());
                const params = {
                    statusID: "0",
                    threadTypeID: "2",
                    prdType: "MM",
                    prdQty: "1",
                    year: currentYear,
                    month: currentMonth,
                };
                const response = await getThreadsAPI(params);
                checkAPIResponse(response);
                if (response?.updatePackage) {
                    const archivedThreads = JSON.parse(response.updatePackage);
                    const dbThreads = archivedThreads.Table1 || archivedThreads.Table || [];
                    const salesThreads = dbThreads.filter(t => {
                        const typeID = getThreadType(t);
                        const isNotDeleted = !t.IsRemove && !t.IsDeleted && !t.isRemove && !t.isDeleted;
                        return (typeID === 2 || typeID === 0) && isNotDeleted;
                    });
                    const totalSalesAmt = salesThreads.reduce((sum, item) => sum + (Number(item.bizProspect || item.BizProspect || item.amount || item.Amt || 0) || 0), 0);
                    setSalesFilterCount(salesThreads.length);
                    setSalesFilterAmount(totalSalesAmt);
                }
            } catch (err) {
                console.error("Failed to fetch initial sales data on Growth page:", err);
            }
        };
        fetchInitialSalesData();
    }, []);

    const tabs = [
        { id: 'inquiries', label: 'Inquiries', count: inquiriesCount, amount: inquiriesAmount },
        { id: 'sales', label: 'Sales', count: salesCount, amount: salesAmount },
        { id: 'contacts', label: 'Contacts', count: contactsCount, amount: contactsAmount }
    ];

    const getPlaceholder = () => {
        switch (activeTab) {
            case 'inquiries': return 'Search inquiries...';
            case 'sales': return 'Search sales...';
            case 'contacts': return 'Search contacts...';
            default: return 'Search...';
        }
    };

    return (
        <div className="flex flex-col w-full bg-bg-grey">
            {/* Top Sub-header Tabs (styled identically to Tasks page) */}
            <div className="w-full sticky top-16 z-30 bg-[#F1F1F0]">
                {showSearch && (
                    <div className="w-full flex items-center gap-2 pt-3">
                        <div className="relative flex-grow">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                value={searchTerm}
                                placeholder={getPlaceholder()}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-10 h-10 w-full"
                                autoFocus
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-3 gap-2 pt-2 pb-1">
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => handleTabChange(tab.id)}
                                className={cn(
                                    "flex flex-col items-start justify-start gap-[10px] rounded-[7px] border bg-white text-left w-full transition-all duration-200 active:scale-95",
                                    "p-[6px] min-[360px]:p-[8px] min-[390px]:p-[10px]",
                                    isActive ? 'text-[var(--sahi-green)] pb-[5px]' : 'text-[#333333] pb-[7px]'
                                )}
                                style={{
                                    borderTop: '1px solid var(--zinc-200, #e4e4e7)',
                                    borderLeft: '1px solid var(--zinc-200, #e4e4e7)',
                                    borderRight: '1px solid var(--zinc-200, #e4e4e7)',
                                    borderBottom: isActive ? '3px solid var(--sahi-green)' : '1px solid var(--zinc-200, #e4e4e7)',
                                    backgroundColor: '#ffffff'
                                }}
                            >
                                <span className="text-[11px] leading-tight font-semibold tracking-tight w-full text-left truncate">
                                    {tab.label}
                                </span>
                                <div className="flex items-baseline justify-start w-full gap-1">
                                    {tab.id === 'contacts' ? (
                                        <span className="text-[18px] font-extrabold text-left leading-normal">
                                            {tab.count}
                                        </span>
                                    ) : (
                                        <span className="text-[16px] tracking-tight min-[360px]:text-[16px] font-extrabold text-left leading-normal truncate">
                                            {currencySymbol} {formatAmount(tab.amount || 0, agencyData?.CountryID || 1)}{" "}
                                            <span className="text-[13px] font-bold opacity-85">
                                                ({tab.count})
                                            </span>
                                        </span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab Content Area - Keep tabs mounted to preserve state and eliminate tab switch lag */}
            <div className="flex-1 w-full">
                <div className={activeTab === 'inquiries' ? 'block' : 'hidden'}>
                    <Threads
                        initialTID={1}
                        embedded={true}
                        activeTab={activeTab}
                        searchTerm={searchTerm}
                        onFilterStateChange={(isActive) => handleFilterStateChange('inquiries', isActive)}
                        onCountChange={(cnt, amt) => {
                            setInquiriesFilterCount(cnt);
                            if (amt !== undefined) setInquiriesFilterAmount(amt);
                        }}
                    />
                </div>
                <div className={activeTab === 'sales' ? 'block' : 'hidden'}>
                    <ThreadsReport
                        initialTID={2}
                        embedded={true}
                        activeTab={activeTab}
                        searchTerm={searchTerm}
                        onFilterStateChange={(isActive) => handleFilterStateChange('sales', isActive)}
                        onCountChange={(cnt, amt) => {
                            setSalesFilterCount(cnt);
                            if (amt !== undefined) setSalesFilterAmount(amt);
                        }}
                    />
                </div>
                <div className={activeTab === 'contacts' ? 'block' : 'hidden'}>
                    <Connections
                        embedded={true}
                        activeTab={activeTab}
                        searchTerm={searchTerm}
                        onFilterStateChange={(isActive) => handleFilterStateChange('contacts', isActive)}
                        onCountChange={(cnt, amt) => {
                            setContactsFilterCount(cnt);
                            if (amt !== undefined) setContactsFilterAmount(amt);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Growth;
